import { DataLakeType } from 'Source/constants';
import { getOperators } from '../constants';

// These are the different SQL data types supported by Snowflake and are therefore the
// possible values returned by the BE
// https://docs.snowflake.com/en/sql-reference/intro-summary-data-types.html
export const numericDataTypes = [
  'NUMBER',
  'DECIMAL',
  'NUMERIC',
  'INT',
  'INTEGER',
  'BIGINT',
  'SMALLINT',
  'TINYINT',
  'BYTEINT',
  'FLOAT',
  'FLOAT4',
  'FLOAT8',
  'DOUBLE',
  'DOUBLE PRECISION',
  'REAL',
].map(item => item.toLowerCase());

export const stringAndBinaryDataTypes = [
  'VARCHAR',
  'CHAR',
  'CHARACTER',
  'STRING',
  'TEXT',
  'BINARY',
  'VARBINARY',
].map(item => item.toLowerCase());

export const logicalDataTypes = ['BOOLEAN'].map(item => item.toLowerCase());

export const dateAndTimeDataTypes = [
  'DATE',
  'DATETIME',
  'TIME',
  'TIMESTAMP',
  'TIMESTAMP_LTZ',
  'TIMESTAMP_NTZ',
  'TIMESTAMP_TZ',
].map(item => item.toLowerCase());

export const semiStructuredDataTypes = ['VARIANT', 'OBJECT', 'ARRAY'].map(item =>
  item.toLowerCase(),
);

export const geospatialDataTypes = ['GEOGRAPHY', 'GEOMETRY'].map(item => item.toLowerCase());

/** Returns an input group based on the passed in columnType. For certain
 * data types, or data types that don't match the list provided by Snowflake, we return null.
 * In the UI, when null is returned from this function, we return ALL filter operators rather
 * than the matching subset.
 */
export const getInputGroup = (columnType: string) => {
  if (numericDataTypes.includes(columnType)) {
    return 'number';
  }

  if (stringAndBinaryDataTypes.includes(columnType)) {
    return 'string';
  }

  if (logicalDataTypes.includes(columnType)) {
    return 'boolean';
  }

  if (dateAndTimeDataTypes.includes(columnType)) {
    return 'time';
  }

  // For the "array" data type we want to return the "array" input group, else we want
  // to return null
  if (semiStructuredDataTypes.includes(columnType)) {
    if (columnType === 'array') {
      return 'array';
    }
    if (columnType === 'object' || columnType === 'variant') {
      return 'object';
    }
    return null;
  }

  if (geospatialDataTypes.includes(columnType)) {
    return null;
  }

  return null;
};

/** Returns a filtered list of operators based on the database table's column type.
 * For certain column types we return the full list of operators. When returning a
 * subset of operators, we always return the "universal" group of operators.
 */
export const getFilteredOperators = (columnType: string, dataLakeType?: DataLakeType | null) => {
  const operators = getOperators(dataLakeType);
  const inputGroup = getInputGroup(columnType);
  // If there's an input group related to the selected column's type, then filter
  // the list of operators down to the ones related to that column type, else
  // return all operators.
  const matchingOperators = inputGroup
    ? // We always want to include the "universal" group
      operators.filter(({ group }) => group === inputGroup || group === 'universal')
    : operators.filter(({ group }) => group !== 'row');

  return matchingOperators;
};
